import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { GalleryItem, LayoutData, SectionMap } from '@/types'

import { Instagram } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { buttonVariants } from '@/components/ui/button'
import { CtaBand } from '@/components/site/cta-band'
import { GalleryGrid } from '@/components/site/gallery-grid'
import { PageHero } from '@/components/site/page-hero'

import {
    getGallery,
    getLayoutData,
    getSections,
    safely
} from '@/lib/data/content'

interface GalleryPageProps {
    layout: LayoutData
    sections: SectionMap
    gallery: Array<GalleryItem>
}

const GalleryPage: FC<GalleryPageProps> = ({ layout, sections, gallery }): ReactNode => (
    <Layout data={layout}>
        <Seo
            title='Gallery'
            siteName={layout.profile.name}
            description='Balayage, bridal makeup, nail art, skin and studio photos from our salon in Indiranagar, Bengaluru.'
            ogImage={gallery[0]?.image ?? layout.profile.hero_image}
            origin={layout.origin}
            path='/gallery'
        />

        <PageHero section={sections.gallery_page} />

        <section id='gallery' className='py-20 sm:py-24'>
            <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                {gallery.length === 0 ? (
                    <p className='rounded-3xl border border-dashed border-border p-12 text-center text-muted-foreground'>
                        Fresh photos are coming soon.
                    </p>
                ) : (
                    <GalleryGrid items={gallery} />
                )}

                {layout.profile.instagram_url && (
                    <div className='mt-14 flex justify-center'>
                        <a href={layout.profile.instagram_url} target='_blank' rel='noopener noreferrer' className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                            <Instagram />
                            See more on Instagram
                        </a>
                    </div>
                )}
            </div>
        </section>

        <CtaBand section={sections.home_cta} profile={layout.profile} />
    </Layout>
)

export const getServerSideProps: GetServerSideProps<GalleryPageProps> = async ({ req }) => {
    const [layout, sections, gallery] = await Promise.all([
        getLayoutData(req),
        safely('sections', () => getSections(['gallery_page', 'home_cta']), {}),
        safely('gallery', getGallery, [])
    ])

    return { props: { layout, sections, gallery } }
}

export default GalleryPage
